/**
 * RMS 세션(모델 ID) 상태 관리 composable
 *
 * 담보관리(RMS) 메뉴에서 사용자가 선택한 TCP 모델 ID를 전역 반응형 상태로 유지한다.
 * 모든 RMS 요청에 axios/fetch interceptor 가 이 값을 X-RMS-Model-Id 헤더로 주입한다.
 *
 * 특징:
 * - 반응형 (Vue 3 ref)
 * - localStorage 에 저장하여 새로고침/탭 전환에도 유지
 * - 기본값: CLI_TRADER_V1 (운영)
 *
 * 사용 예:
 *   import { useRmsSession } from '@/composables/useRmsSession'
 *   const { modelId, setModelId, availableModels, refreshStatus } = useRmsSession()
 */
import { ref, readonly } from 'vue'
import axios from 'axios'

/** RMS 요청 헤더 이름 (백엔드 RmsTrHelper.HEADER_RMS_MODEL_ID 와 일치) */
export const RMS_MODEL_ID_HEADER = 'X-RMS-Model-Id'

/** localStorage 키 */
const STORAGE_KEY = 'rms.session.modelId'

/** 기본 모델 ID (백엔드 rms.default-model-id 와 동일) */
const DEFAULT_MODEL_ID = 'CLI_TRADER_V1'

/**
 * 과거 dev 빌드에서 하드코딩된 jhg5880 전용 V3가 localStorage 에 남아있는
 * 브라우저는 78/운영에서 닿지 않는 사설 IP 엔드포인트로 TR 을 보내게 된다.
 * 한 번만 정리한 뒤 운영 기본값(V1) 로 폴백한다.
 */
const LEGACY_DEV_MODEL_IDS = new Set(['CLI_TRADER_V3'])

// ---------- 전역 싱글톤 상태 (composable 호출 시 공유됨) ----------
const modelId = ref(loadFromStorage())
const availableModels = ref([])   // [{modelId, name, role, enabledYn, status}]
const currentStatus = ref('UNKNOWN')  // RUNNING | STOPPED | UNKNOWN

function loadFromStorage() {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v && LEGACY_DEV_MODEL_IDS.has(v)) {
      localStorage.removeItem(STORAGE_KEY)
      return DEFAULT_MODEL_ID
    }
    return (v && v.length > 0) ? v : DEFAULT_MODEL_ID
  } catch (e) {
    return DEFAULT_MODEL_ID
  }
}

function saveToStorage(v) {
  try {
    if (v) localStorage.setItem(STORAGE_KEY, v)
  } catch (e) {
    // private 모드 등 — 무시
  }
}

// ---------- contextPath 해석 (배포 경로 호환) ----------
function contextPath() {
  const marker = '/app/'
  const path = window.location.pathname
  const idx = path.indexOf(marker)
  return idx > 0 ? path.substring(0, idx) : ''
}

// ---------- API 호출 ----------

/**
 * 사용 가능한 TCP 모델 목록을 조회해 availableModels 에 반영한다.
 */
async function fetchAvailableModels() {
  try {
    // 백엔드 규약: POST /api/tcp-models 가 전체 목록 반환
    const r = await axios.post(`${contextPath()}/api/tcp-models`, {}, {
      withCredentials: true,
      headers: { Accept: 'application/json' }
    })
    const list = Array.isArray(r.data) ? r.data : (r.data?.data || [])
    availableModels.value = list
      .filter(m => m.enabledYn === 'Y' || m.enabledYn === undefined)
      .map(m => ({
        modelId: m.modelId,
        name: m.name || m.modelId,
        role: m.role,
        descTxt: m.descTxt || '',
        enabledYn: m.enabledYn
      }))
    return availableModels.value
  } catch (e) {
    console.warn('[useRmsSession] 모델 목록 조회 실패:', e?.message || e)
    availableModels.value = []
    return []
  }
}

/**
 * 현재 선택된 모델의 실행 상태(RUNNING/STOPPED) 를 조회한다.
 */
async function refreshStatus(id) {
  const target = id || modelId.value
  if (!target) { currentStatus.value = 'UNKNOWN'; return }
  try {
    const r = await axios.post(
      `${contextPath()}/api/tcp/client/${encodeURIComponent(target)}/status`,
      {},
      { withCredentials: true, headers: { Accept: 'application/json' } }
    )
    const s = r.data?.status || 'UNKNOWN'
    currentStatus.value = s
    return s
  } catch (e) {
    console.warn('[useRmsSession] 상태 조회 실패:', e?.message || e)
    currentStatus.value = 'UNKNOWN'
    return 'UNKNOWN'
  }
}

/**
 * 사용할 모델 ID 를 변경한다.
 *   - localStorage 에 저장
 *   - 변경 후 상태 재조회
 */
function setModelId(id) {
  if (!id || id === modelId.value) return
  modelId.value = id
  saveToStorage(id)
  refreshStatus(id)
}

// ---------- V77 (Phase 4) — 상품 기반 TCP 모델 자동 적용 ----------
/** 강제 고정(MODEL_LOCK_YN='Y') 여부 — 사용자 셀렉터 비활성용. */
const modelLocked = ref(false)

/**
 * V77 (Phase 4) — 상품 선택 시 RMS 매핑 정보 조회 후 TCP 모델 자동 적용.
 *
 * <p>모델 정책에 따라 동작:</p>
 * <ul>
 *   <li>MODEL_LOCK_YN='Y' 강제 고정 — defaultTcpModel 로 set + modelLocked=true (사용자 변경 차단)</li>
 *   <li>MODEL_LOCK_YN='N' override 허용 — defaultTcpModel 로 set + modelLocked=false (사용자 변경 가능)</li>
 *   <li>PUMCODE 미등록 — 폴백(CLI_TRADER_V1) 적용 + modelLocked=false</li>
 * </ul>
 *
 * @param {string} pumcode 상품코드
 */
async function setModelByProduct(pumcode) {
  if (!pumcode) { modelLocked.value = false; return null }
  try {
    const r = await axios.get(
      `${contextPath()}/api/products/stock-loan/${encodeURIComponent(pumcode)}/rms-mapping`,
      { withCredentials: true }
    )
    const m = r.data || {}
    const target = m.defaultTcpModel || DEFAULT_MODEL_ID
    setModelId(target)
    modelLocked.value = (m.modelLockYn === 'Y')
    return m
  } catch (e) {
    console.warn('[useRmsSession] 상품 매핑 조회 실패:', e?.message || e)
    modelLocked.value = false
    return null
  }
}

/**
 * 현재 선택된 모델 ID를 반환 (axios interceptor 등 비반응형 컨텍스트용).
 */
function getModelId() {
  return modelId.value
}

/** composable 팩토리. */
export function useRmsSession() {
  return {
    // 반응형 상태 (읽기 전용으로 노출)
    modelId: readonly(modelId),
    availableModels: readonly(availableModels),
    currentStatus: readonly(currentStatus),
    modelLocked: readonly(modelLocked), // V77 Phase 4 - 강제 고정 여부

    // 액션
    setModelId,
    getModelId,
    fetchAvailableModels,
    refreshStatus,
    setModelByProduct // V77 Phase 4 - 상품 기반 자동 매핑
  }
}

/** interceptor 등 외부에서 직접 쓰는 경우를 위한 헬퍼. */
export function getCurrentRmsModelId() {
  return modelId.value
}
